import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, BarChart3, Users, Coins, Cpu, Sparkles, Wand2, Terminal, FileText, Key, Shield, Loader2, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import axiosInstance from '../lib/axios';
import { useAuthStore } from '../store/authStore';
import AdminDashboard from '../tiptap/components/AdminDashboard';
import ModelsPricingCard from '../tiptap/components/ModelsPricingCard';
import PromptsCard from '../tiptap/components/PromptsCard';
import LegalTextsCard from '../tiptap/components/LegalTextsCard';
import AdminSettings from '../components/AdminSettings';
import ChangePasswordCard from '../components/ChangePasswordCard';

/** Master prompt (mejora de prompts) — bloque propio para la sección "Mejora de prompts". */
function MasterPromptCard() {
  const [masterPrompt, setMasterPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axiosInstance.get('/auth/master-prompt');
        setMasterPrompt(r.data.master_prompt || '');
      } catch { /* 403 lo maneja el interceptor */ } finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    if (!masterPrompt.trim()) { toast.error('El master prompt no puede estar vacío'); return; }
    setSaving(true);
    try {
      await axiosInstance.put('/auth/master-prompt', null, { params: { master_prompt: masterPrompt } });
      toast.success('Master prompt guardado');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); } finally { setSaving(false); }
  };

  return (
    <div className="glass rounded-xl p-6 space-y-3">
      <label className="text-sm font-medium flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-purple-600" /> Master Prompt (mejora de prompts)
      </label>
      <Textarea rows={10} value={masterPrompt} disabled={loading} onChange={(e) => setMasterPrompt(e.target.value)}
        placeholder="Eres un experto en diseño de prompts…" className="resize-none font-mono text-sm" />
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Se aplica cuando los usuarios hacen clic en "Mejorar con IA".
      </p>
      <Button onClick={save} disabled={saving || loading}
        className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Guardar
      </Button>
    </div>
  );
}

// Menú lateral: cada opción -> su contenido en el área principal.
const SECTIONS = [
  { key: 'stats', label: 'Estadísticas', icon: BarChart3, render: () => <AdminDashboard section="stats" /> },
  { key: 'users', label: 'Usuarios', icon: Users, render: () => <AdminDashboard section="users" /> },
  { key: 'credits', label: 'Créditos', icon: Coins, render: () => <AdminDashboard section="credits" /> },
  { key: 'models', label: 'Modelos y precios', icon: Cpu, render: () => <ModelsPricingCard /> },
  { key: 'prompts', label: 'Prompts del sistema', icon: Sparkles, render: () => <PromptsCard /> },
  { key: 'master', label: 'Mejora de prompts', icon: Wand2, render: () => <MasterPromptCard /> },
  { key: 'commands', label: 'Comandos Rápidos de IA', icon: Terminal, render: () => <AdminSettings section="commands" /> },
  { key: 'legal', label: 'Textos legales', icon: FileText, render: () => <LegalTextsCard /> },
  { key: 'key', label: 'Llave OpenRouter', icon: Key, render: () => <AdminSettings section="key" /> },
  { key: 'security', label: 'Seguridad', icon: Lock, render: () => <ChangePasswordCard /> },
];

export default function AdminPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const [active, setActive] = useState('stats');

  if (user && user.role !== 'admin') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
        <Shield className="w-16 h-16 text-slate-300 dark:text-slate-600" />
        <p className="text-slate-600 dark:text-slate-300">Solo administradores pueden acceder a esta área.</p>
        <Button variant="outline" onClick={() => navigate('/dashboard')}>Volver</Button>
      </div>
    );
  }

  const current = SECTIONS.find((s) => s.key === active) || SECTIONS[0];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <header className="sticky top-0 z-20 border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur">
        <div className="px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(location.state?.from || '/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <div className="flex items-center gap-2 font-semibold">
            <Shield className="w-5 h-5 text-purple-600" /> Administración
          </div>
        </div>
      </header>

      <div className="flex flex-col md:flex-row">
        {/* Menú lateral — móvil: barra horizontal scrolleable arriba; desktop: sidebar vertical */}
        <nav className="md:hidden flex gap-2 overflow-x-auto pb-2 px-3 border-b border-slate-200 dark:border-slate-800 bg-white/50 dark:bg-slate-900/40">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const isActive = s.key === active;
            return (
              <button
                key={s.key}
                data-testid={`admin-nav-mobile-${s.key}`}
                onClick={() => setActive(s.key)}
                className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs whitespace-nowrap transition-colors ${
                  isActive
                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200 font-medium'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                <Icon className="w-3.5 h-3.5 shrink-0" /> {s.label}
              </button>
            );
          })}
        </nav>
        <nav className="hidden md:block w-56 shrink-0 border-r border-slate-200 dark:border-slate-800 min-h-[calc(100vh-57px)] p-3 space-y-1 bg-white/50 dark:bg-slate-900/40">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const isActive = s.key === active;
            return (
              <button
                key={s.key}
                data-testid={`admin-nav-${s.key}`}
                onClick={() => setActive(s.key)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                  isActive
                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200 font-medium'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" /> {s.label}
              </button>
            );
          })}
        </nav>

        {/* Contenido de la opción activa */}
        <main className="flex-1 min-w-0 p-3 sm:p-6">
          <div className="max-w-4xl">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <current.icon className="w-5 h-5 text-purple-600" /> {current.label}
            </h2>
            {current.render()}
          </div>
        </main>
      </div>
    </div>
  );
}
